// Per-route <head> for a single-page app, plus the runtime structured data.
//
// ── What is worth being honest about here ────────────────────────────────────
// Google renders JavaScript and reads structured data that is in the DOM by the
// time it does: "Google Search can understand and process structured data that's
// available in the DOM when it renders the page" (Google's own Generate
// Structured Data with JavaScript guide). So the ItemList below is genuinely
// read. Two limits come with that and neither is worth pretending away:
//
//   1. It lands in the SECOND pass, after the render queue, so it is always
//      slower to take effect than markup served in the shell.
//   2. Crawlers that execute no JavaScript never see it at all. That now
//      includes the AI ones people actually ask for recommendations (GPTBot,
//      ClaudeBot, PerplexityBot). Which is why the site-level WebSite and
//      Organization markup lives statically in index.html instead.
//
// ── Why this is an ItemList and not a pile of Events ─────────────────────────
// Google's Event documentation is explicit that the event experience "only
// supports pages that focus on a single event". A listing page cannot earn event
// rich results however it is marked up, so emitting 400 Event objects at the top
// level would be cargo cult: more bytes, same zero rich results.
//
// What is emitted is an ItemList, which is what this page honestly IS, with each
// entry carrying its Event. That is valid schema.org, it is useful to anything
// reading the page as data rather than for a Google rich result, and it does not
// claim an eligibility the page does not have.
//
// The unlock for actual event rich results is per-event URLs plus prerendering
// so each one is a real page. That is a much bigger change than this file and is
// flagged rather than started.

import { useEffect } from 'react'
import type { EventRow } from '../types'
import type { RouteName } from './route'
import { localityForVenue } from './areas'

const ORIGIN = 'https://calendar.lyhevents.com'
const LD_ID = 'lyh-runtime-jsonld'

/** How many events to describe. The whole table is ~450 rows and most are weeks
    out; the page is about what is on soon, and a smaller payload renders faster
    in the crawl queue that already runs late. */
const MAX_LD_EVENTS = 50

interface RouteMeta {
  title: string
  description: string
  path: string
}

const META: Record<RouteName, RouteMeta> = {
  calendar: {
    title: 'Lynchburg VA Events Calendar | LYH Events',
    description:
      "What's on around Lynchburg, Virginia this week: live music, trivia, markets, classes and family events. New listings go up every Tuesday.",
    path: '/',
  },
  stories: {
    title: 'Your Stories | LYH Events Lynchburg',
    description:
      'Neighbours on what they found around Lynchburg, and the people they met doing it. Share your own, with your name or without it.',
    path: '/stories',
  },
  groups: {
    title: 'Community Groups in Lynchburg VA | LYH Events',
    description:
      'Groups around Lynchburg you can actually join: run and hiking clubs, board games, crafts, choirs, dance nights, book clubs and volunteering. Every link checked.',
    path: '/groups',
  },
  resources: {
    title: 'Disaster Resources | LYH Events Lynchburg',
    description:
      'Where to find help around Lynchburg during severe weather, a power outage, or a bad air-quality day: alert signup, free wifi, help lines beyond 911, and warming and cooling centers.',
    path: '/resources',
  },
  vendors: {
    title: 'Hosts & Vendors | LYH Events Lynchburg',
    description:
      'Markets and events around Lynchburg looking for vendors, food trucks and artisans, plus a way for hosts to post a call of their own.',
    path: '/vendors',
  },
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
}

function eventToLd(e: EventRow) {
  const ld: Record<string, unknown> = {
    '@type': 'Event',
    name: e.name,
    startDate: e.start_time,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    // Every event points at the calendar, since none of them has a page of its
    // own yet. When they do, this becomes the per-event URL and the whole
    // single-event question above reopens in a good way.
    url: e.link ?? `${ORIGIN}/`,
    location: {
      '@type': 'Place',
      name: e.venue ?? 'Lynchburg, Virginia',
      address: {
        '@type': 'PostalAddress',
        // The venue column is free text ("Apocalypse Ale Works (Forest)", "1219
        // Main St"), so there is no street/locality split to hand over. Sending
        // the whole string as streetAddress and deriving the locality is the
        // most that is TRUE here; inventing a parsed address would be worse than
        // an incomplete one.
        //
        // localityForVenue rather than the rule this file used to inline, which
        // collapsed neighborhoods into the city and returned the AREA for
        // everywhere else: an Afton winery published as "Nelson", a county with
        // no town of that name in it, and Moneta as "Bedford". Right for a
        // filter row, wrong in an address. It
        // is imported now, because the copy here and the copy in the SEO
        // generator had already drifted apart from areas.ts by two whole town
        // lists.
        streetAddress: e.venue ?? undefined,
        addressLocality: localityForVenue(e.venue),
        addressRegion: 'VA',
        addressCountry: 'US',
      },
    },
  }
  if (e.end_time) ld.endDate = e.end_time
  if (e.note) ld.description = e.note
  // Only free events get an offer. price_text is free form ("$25-35", "from
  // $10") and a mis-parsed number is a wrong price on a public listing, which is
  // worse than no price at all - the same rule the cost chips already follow.
  if (e.cost_type === 'free') {
    ld.offers = {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: e.link ?? `${ORIGIN}/`,
    }
  }
  return ld
}

/**
 * The ItemList for a set of events, or null when there is nothing upcoming to
 * describe. Split out from the DOM write so it can be exercised without a
 * browser or a live database.
 */
export function buildEventsLd(
  events: EventRow[],
  now: number = Date.now(),
): Record<string, unknown> | null {
  const upcoming = events
    .filter((e) => {
      const t = new Date(e.start_time).getTime()
      return !Number.isNaN(t) && t >= now
    })
    .sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
    )
    .slice(0, MAX_LD_EVENTS)
  if (upcoming.length === 0) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Upcoming events in Lynchburg, Virginia',
    numberOfItems: upcoming.length,
    itemListElement: upcoming.map((e, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: eventToLd(e),
    })),
  }
}

function writeEventsLd(events: EventRow[]) {
  const existing = document.getElementById(LD_ID)
  if (existing) existing.remove()

  const ld = buildEventsLd(events)
  if (!ld) return

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = LD_ID
  script.textContent = JSON.stringify(ld)
  document.head.appendChild(script)
}

/**
 * Keep the head in step with the route, and publish the events as data.
 *
 * The canonical is built from the ROUTE, never from location.href: the calendar
 * writes its filters into the query string, so mirroring the live URL would tell
 * a crawler that ?cost=free and ?hosts=karaoke are separate pages and split the
 * same page into dozens of near-duplicates.
 */
export function useDocumentHead(route: RouteName, events: EventRow[]) {
  useEffect(() => {
    const meta = META[route]
    const url = `${ORIGIN}${meta.path}`

    document.title = meta.title
    setMeta('meta[name="description"]', 'name', 'description', meta.description)
    setMeta('meta[property="og:title"]', 'property', 'og:title', meta.title)
    setMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      meta.description,
    )
    setMeta('meta[property="og:url"]', 'property', 'og:url', url)
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', meta.title)
    setMeta(
      'meta[name="twitter:description"]',
      'name',
      'twitter:description',
      meta.description,
    )

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = url
  }, [route])

  useEffect(() => {
    // Events belong to the calendar. On /stories or /groups the list would
    // describe something the page is not showing.
    writeEventsLd(route === 'calendar' ? events : [])
  }, [route, events])
}
